import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	ArchiveIcon,
	Avatar,
	Badge,
	CloseIcon,
	ContextMenu,
	IconButton,
	LabelPlus,
	MoreAlternateIcon,
	Paragraph,
	SendIcon,
	Sidebar,
	Spinner,
	UndoIcon,
} from '@treams/treams-ui';

const Row = styled.div`
	width: 100%;

	a {
		display: block;
		width: 100%;
	}
`;

export default function FeedbackRecipient(props: {
	request: graphql.FeedbackRequest;
	response: graphql.FeedbackResponse;
}) {
	const { t } = useTranslation();

	const [hoverContainer, setHoverContainer] = useState(false);
	const [hoverMore, setHoverMore] = useState(false);

	const [cancel, { loading: loadingCancel }] = graphql.useArchiveFeedbackResponseMutation({
		variables: { input: { responseID: props.response.id } },
	});

	const [restore, { loading: loadingRestore }] = graphql.useRestoreFeedbackResponseMutation({
		variables: { input: { responseID: props.response.id } },
	});

	const [resend, { loading: loadingResend }] = graphql.useSendFeedbackReminderMutation({
		variables: { input: { responseID: props.response.id } },
	});

	return (
		<Row>
			<Sidebar.Item
				hover={hoverContainer && !hoverMore}
				path={[
					props.request.user.isViewer
						? `/feedback/${props.request.id}`
						: `/users/${props.request.user.id}/feedback/${props.request.id}`,
					`response_id=${props.response.id}`,
				].join('?')}
				onMouseEnter={() => setHoverContainer(true)}
				onMouseLeave={() => setHoverContainer(false)}
			>
				<Badge.Container
					badge={
						<>
							{props.response.status === graphql.FeedbackResponseStatus.Shared && <Badge.Success />}
							{props.response.status === graphql.FeedbackResponseStatus.Draft && <Badge.Pending />}
							{props.response.status === graphql.FeedbackResponseStatus.Cancelled && (
								<Badge.Error />
							)}
						</>
					}
					mr="12px"
				>
					<Avatar user={props.response.recipient} size={36} flexGrow={0} flexShrink={0} />
				</Badge.Container>
				<Box flexGrow={1} flexShrink={1} minWidth={0}>
					<LabelPlus>
						{props.response.recipient.firstName} {props.response.recipient.lastName}
					</LabelPlus>
					<Paragraph small>{props.response.recipient.email}</Paragraph>
				</Box>
				{loadingCancel || loadingResend || loadingRestore ? (
					<Flex width={32} height={32} alignItems="center" justifyContent="center">
						<Spinner />
					</Flex>
				) : (
					<Box flexGrow={0} flexShrink={0}>
						<ContextMenu.Target
							menu={
								<ContextMenu>
									{props.response.status === graphql.FeedbackResponseStatus.Cancelled && (
										<ContextMenu.Item
											onClick={() => restore()}
											icon={<UndoIcon />}
											title={t('feedback.recipient.restore')}
										/>
									)}
									{props.response.status === graphql.FeedbackResponseStatus.Draft && (
										<>
											<ContextMenu.Item
												onClick={() => resend()}
												icon={<SendIcon />}
												title={t('feedback.recipient.resend')}
											/>
											<ContextMenu.Item
												onClick={() => cancel()}
												icon={<CloseIcon />}
												title={t('feedback.recipient.cancel')}
											/>
										</>
									)}
									{props.response.status === graphql.FeedbackResponseStatus.Shared && (
										<ContextMenu.Item
											onClick={() => cancel()}
											icon={<ArchiveIcon />}
											title={t('feedback.recipient.archive')}
										/>
									)}
								</ContextMenu>
							}
						>
							<Box onMouseEnter={() => setHoverMore(true)} onMouseLeave={() => setHoverMore(false)}>
								<IconButton type="secondary" icon={<MoreAlternateIcon />} />
							</Box>
						</ContextMenu.Target>
					</Box>
				)}
			</Sidebar.Item>
		</Row>
	);
}

function FeedbackRecipientSkeleton() {
	return (
		<Sidebar.Custom>
			<Avatar.Skeleton size={36} mr={3} flexGrow={0} flexShrink={0} />
			<Box flexGrow={1} flexShrink={1}>
				<LabelPlus.Skeleton />
				<Paragraph.Skeleton small mt={1} />
			</Box>
			<IconButton.Skeleton />
		</Sidebar.Custom>
	);
}

FeedbackRecipient.Skeleton = FeedbackRecipientSkeleton;
